import { ref, watch } from "vue";
import api from "@/servicios/apiCliente";
import { Cliente } from "@/servicios/api";

type Contrato = Cliente.Financiamiento.InformacionFinanciamientoContrato;

/**
 * Datos del "Centro de Gestión del Estado de Cuenta" del home.
 * Extraído de CCHome.vue para reutilizarlo en el rediseño.
 */
export function useEstadoCuenta() {
  const listaContratos = ref([] as Contrato[]);
  const contratoSeleccionado = ref(undefined as Contrato | undefined);
  const banners = ref([] as Cliente.Models.BannerCampania[]);

  const fechaLimite = ref("");
  const totalPagar = ref(undefined as number | undefined);
  const referencia = ref("");
  const linkDescargaComplemento = ref("");

  // Al cambiar de contrato se refrescan monto y enlace de descarga.
  watch(contratoSeleccionado, contrato => {
    if (!contrato) return;
    totalPagar.value = contrato.totalPagar;

    // Si el contrato no trae enlace propio se conserva el general, en lugar
    // de dejar el botón Consultar sin destino.
    const enlaceContrato = contrato.linkEstadoCuentaContrato?.find(d => d != null);
    if (enlaceContrato) linkDescargaComplemento.value = enlaceContrato;
  });

  function formatearFecha(fecha: string | Date) {
    return new Intl.DateTimeFormat("es-MX", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    }).format(new Date(fecha));
  }

  async function cargar() {
    const [respHome, respFinanciamiento, respContratos] = await Promise.all([
      api.Cliente.Cliente.HomeApi.ObtenerInformacionHome(),
      api.Cliente.Cliente.FinanciamientoApi.ObtenerInformacion(1),
      api.Cliente.Cliente.FinanciamientoApi.ObtenerContratos(1)
    ]);

    banners.value = respHome.data.banners ?? [];

    // Datos generales del financiamiento: fecha límite y referencia no
    // dependen del contrato elegido.
    const informacion = respFinanciamiento.data;
    if (informacion?.linkDescargaComplemento) {
      totalPagar.value = informacion.totalPagar;
      fechaLimite.value = formatearFecha(informacion.fechaVigencia);
      referencia.value = informacion.referenciaUnica ?? "";
      linkDescargaComplemento.value = informacion.linkDescargaComplemento;
    }

    // Se asigna al final: el watch sobrescribe monto y enlace con los del
    // contrato seleccionado, que debe mandar sobre los datos generales.
    listaContratos.value = respContratos.data ?? [];
    contratoSeleccionado.value = listaContratos.value[0];

    return respHome.data;
  }

  return {
    listaContratos,
    contratoSeleccionado,
    banners,
    fechaLimite,
    totalPagar,
    referencia,
    linkDescargaComplemento,
    cargar
  };
}
